import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth, API } from "@/App";
import { useWebSocket } from "@/contexts/WebSocketContext";

export default function Chat() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sendTyping, typingUsers } = useWebSocket();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [matchData, setMatchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const fetchMatchData = useCallback(async () => {
    try {
      const response = await fetch(`${API}/matches`, {
        credentials: "include",
      });
      if (response.ok) {
        const matches = await response.json();
        const match = matches.find((m) => m.match_id === matchId);
        if (match) {
          setMatchData(match);
        }
      }
    } catch (error) {
      // Match data enhances UI but chat still works without it
    }
  }, [matchId]);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await fetch(`${API}/messages/${matchId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    fetchMessages();
    fetchMatchData();

    // Listen for new messages via WebSocket
    const handleNewMessage = (event) => {
      const message = event.detail;
      if (message.match_id === matchId) {
        setMessages((prev) => {
          // Avoid duplicates
          if (prev.find((m) => m.message_id === message.message_id)) {
            return prev;
          }
          return [...prev, message];
        });
      }
    };

    window.addEventListener("new_message", handleNewMessage);
    
    // Poll for new messages every 5 seconds as fallback
    const pollInterval = setInterval(() => {
      fetchMessages();
    }, 5000);
    
    return () => {
      window.removeEventListener("new_message", handleNewMessage);
      clearInterval(pollInterval);
    };
  }, [matchId, fetchMessages, fetchMatchData]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !matchData) return;

    const receiverId = matchData.user.user_id;
    const messageContent = newMessage.trim();
    setNewMessage("");

    // Optimistically add message
    const tempId = `temp_${Date.now()}`;
    const tempMessage = {
      message_id: tempId,
      match_id: matchId,
      sender_id: user?.user_id,
      content: messageContent,
      created_at: new Date().toISOString(),
      pending: true,
    };
    setMessages((prev) => [...prev, tempMessage]);

    try {
      const response = await fetch(`${API}/messages/${matchId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          receiver_id: receiverId,
          content: messageContent,
        }),
      });

      if (response.ok) {
        const msg = await response.json();
        // Replace temp message with real one
        setMessages((prev) =>
          prev.map((m) => (m.message_id === tempId ? { ...msg, pending: false } : m))
        );
      }
    } catch (error) {
      toast.error("Failed to send message");
      setMessages((prev) => prev.filter((m) => m.message_id !== tempId));
    }

    // Stop typing indicator
    sendTyping(matchId, false);
    inputRef.current?.focus();
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    sendTyping(matchId, true);
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Stop typing after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      sendTyping(matchId, false);
    }, 2000);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const isTyping = typingUsers[matchId] && typingUsers[matchId] !== user?.user_id;

  return (
    <div className="min-h-screen bg-[#FFF5E1] flex flex-col">
      {/* Header */}
      <header className="app-header">
        <div className="container mx-auto px-4">
          <div className="flex items-center h-16 gap-4">
            <button
              onClick={() => navigate("/matches")}
              className="brutal-btn bg-white p-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            
            {matchData && (
              <div
                className="flex items-center gap-3 cursor-pointer"
                onClick={() => navigate(`/profile/${matchData.user.user_id}`)}
              >
                <div className="relative">
                  <div className="w-10 h-10 border-2 border-black bg-gray-200 overflow-hidden">
                    {matchData.user.picture ? (
                      <img
                        src={matchData.user.picture}
                        alt={matchData.user.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-black">
                        {matchData.user.name?.[0]}
                      </div>
                    )}
                  </div>
                  {/* Online indicator */}
                  {matchData.user.is_online && (
                    <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-[#00CC99] border-2 border-black rounded-full"></span>
                  )}
                </div>
                <div>
                  <span className="font-bold text-lg" data-testid="chat-user-name">
                    {matchData.user.name}
                  </span>
                  {matchData.user.is_online && (
                    <p className="text-xs text-[#00CC99] font-medium">Online</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto p-4">
        <div className="container mx-auto max-w-2xl space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-black border-t-transparent rounded-full"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8" data-testid="no-messages">
              <p className="text-[#4A4A4A]">No messages yet. Say hi!</p>
            </div>
          ) : (
            messages.map((msg, index) => {
              const isSent = msg.sender_id === user?.user_id;
              return (
                <motion.div
                  key={msg.message_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.02 }}
                  className={`flex ${isSent ? "justify-end" : "justify-start"}`}
                  data-testid={`message-${msg.message_id}`}
                >
                  <div className={`${isSent ? "chat-bubble-sent" : "chat-bubble-received"} ${msg.pending ? "opacity-60" : ""}`}>
                    <p>{msg.content}</p>
                    <p className={`text-xs mt-1 ${isSent ? "text-white/70" : "text-black/50"}`}>
                      {new Date(msg.created_at).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                      {msg.pending && " • Sending..."}
                    </p>
                  </div>
                </motion.div>
              );
            })
          )}
          
          {/* Typing Indicator */}
          {isTyping && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
            >
              <div className="chat-bubble-received">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-[#4A4A4A] rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                  <span className="w-2 h-2 bg-[#4A4A4A] rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                  <span className="w-2 h-2 bg-[#4A4A4A] rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Message Input */}
      <div className="border-t-2 border-black bg-white p-4">
        <form onSubmit={sendMessage} className="container mx-auto max-w-2xl">
          <div className="flex gap-3">
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={handleTyping}
              placeholder="Type a message..."
              className="brutal-input flex-1"
              data-testid="message-input"
            />
            <button
              type="submit"
              disabled={!newMessage.trim()}
              className="brutal-btn-primary disabled:opacity-50"
              data-testid="send-btn"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
